import { Segment, TimeRange } from '@mi-front/core';

/**
 * Represents a time range where clicks should be disabled.
 */
export interface UnclickableRange {
  readonly from: number;
  readonly to: number;
  readonly length: number;
}

/**
 * Computes the gaps between segments that should be un-clickable.
 *
 * Returns an array of ranges representing:
 * - The gap before the first segment (if any)
 * - Gaps between consecutive segments
 * - The gap after the last segment (if any)
 *
 * @param segments - Array of segments sorted by startTs
 * @param range - The time range being displayed
 * @returns Array of un-clickable ranges
 */
export function computeGapsBetweenSegments(
  segments: readonly Segment[],
  range: TimeRange<number>,
): UnclickableRange[] {
  if (segments.length === 0) {
    // If no segments, entire range is un-clickable
    return [{ from: range.from, to: range.to, length: range.to - range.from }];
  }

  const gaps: UnclickableRange[] = [];

  // Sort segments by startTs to ensure correct gap calculation
  const sortedSegments = [...segments].sort((a, b) => a.startTs - b.startTs);

  // Gap before first segment (if any)
  if (sortedSegments[0].startTs > range.from) {
    const gapLength = sortedSegments[0].startTs - range.from;
    gaps.push({
      from: range.from,
      to: sortedSegments[0].startTs,
      length: gapLength,
    });
  }

  // Gaps between consecutive segments
  for (let i = 0; i < sortedSegments.length - 1; i++) {
    const currentEnd = sortedSegments[i].endTs;
    const nextStart = sortedSegments[i + 1].startTs;

    // Only add gap if there's actually a space between segments
    if (nextStart > currentEnd) {
      const gapLength = nextStart - currentEnd;
      gaps.push({
        from: currentEnd,
        to: nextStart,
        length: gapLength,
      });
    }
  }

  // Gap after last segment (if any)
  const lastSegment = sortedSegments[sortedSegments.length - 1];
  if (lastSegment.endTs < range.to) {
    const gapLength = range.to - lastSegment.endTs;
    gaps.push({
      from: lastSegment.endTs,
      to: range.to,
      length: gapLength,
    });
  }

  return gaps;
}

/**
 * Checks if a timestamp falls within any of the un-clickable ranges.
 *
 * @param ts - Timestamp to check
 * @param unclickableRanges - Array of un-clickable ranges
 * @returns true if the timestamp is in an un-clickable range
 */
export function isTimestampUnclickable(
  ts: number,
  unclickableRanges: readonly UnclickableRange[],
): boolean {
  return unclickableRanges.some((range) => ts >= range.from && ts <= range.to);
}
